import React, {useEffect, useState} from 'react';
import clsx from 'clsx';
import {CategoryAutoList} from '@netx/core-category-next';
import {SelectField} from '@netx/core-components/SelectField';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {SearchSubTypeLabel, SearchRuleWarningLabel} from '../../../../locales';
import type {CommonRuleEditorProps} from '../../../types';
import {CategoryType} from '../../constants';
import {getCategoryIdTypeLabel} from '../../utils';
import {
	categoryIdRuleEditorClasses,
	getCategoryIdRuleEditorUtilityClass,
	CategoryIdRuleEditorClasses,
	CATEGORY_ID_RULE_EDITOR_CLASS_NAME,
} from './categoryIdRuleEditorClasses';

export type CategoryIdRuleEditorOwnerState = WithClasses<
	CategoryIdRuleEditorClasses,
	{}
>;

const useUtilityClasses = (ownerState: CategoryIdRuleEditorOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCategoryIdRuleEditorUtilityClass, classes);
};

const CategoryIdRuleEditorRoot = styled('div', {
	name: CATEGORY_ID_RULE_EDITOR_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'flex',
	flex: '1 1 auto',
	'& > * + *': {
		marginLeft: theme.spacing(1),
	},
}));

export type CategoryIdRuleEditorProps = WithClasses<
	CategoryIdRuleEditorClasses,
	Pick<CommonRuleEditorProps, 'validationErrors'> & {
		elementSubType1: CategoryType;
		elementSubType1Options: CategoryType[];
		elementValue1: number;
		elementValue1Unavailable: boolean;
		elementValue1Label: string;
		elementValue1Loading: boolean;
		onSubType1Change: (subType1: CategoryType) => void;
		onValue1Change: (value: number) => void;
	}
>;

const CategoryIdRuleEditorInner = ({
	classes: _classes,
	className,
	elementSubType1,
	elementSubType1Options,
	elementValue1,
	elementValue1Label,
	elementValue1Loading,
	elementValue1Unavailable,
	validationErrors,
	onSubType1Change,
	onValue1Change,
}: CategoryIdRuleEditorProps) => {
	const ownerState: CategoryIdRuleEditorOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const [categoryName, setCategoryName] = useState('');

	const waitingForCategory = elementValue1Loading && categoryName === '';

	useEffect(() => {
		setCategoryName(elementValue1Label);
	}, [elementValue1Label]);

	return (
		<CategoryIdRuleEditorRoot className={clsx(classes.root, className)}>
			<SelectField<CategoryType, false, true, false>
				data-testid="categoryid-rule-sub-type-1"
				disableClearable
				label={SearchSubTypeLabel.MATCH_TYPE}
				name={'rule-sub-type-1'}
				options={elementSubType1Options}
				value={elementSubType1}
				getOptionLabel={getCategoryIdTypeLabel}
				onChange={(event, value) => onSubType1Change(value)}
			/>

			<CategoryAutoList<false, true>
				data-testid="categoryid-rule-value-1"
				disableClearable
				error={validationErrors?.elementValue1?.message}
				loadingValue={elementValue1Loading}
				multiple={false}
				name={'rule-value-1'}
				placeholder={
					waitingForCategory
						? 'loading...'
						: elementValue1Unavailable
						? SearchRuleWarningLabel.DATA_UNAVAILABLE
						: undefined
				}
				value={
					elementValue1Unavailable || waitingForCategory
						? (null as any)
						: {
								label: categoryName,
								value: elementValue1,
						  }
				}
				onChange={(event, value) => {
					if (!value) return;
					setCategoryName(value.label);
					onValue1Change(value.value);
				}}
				InputProps={{
					InputLabelProps: {
						shrink: elementValue1Unavailable || undefined,
					},
				}}
			/>
		</CategoryIdRuleEditorRoot>
	);
};

export const CategoryIdRuleEditorPrimitive = CategoryIdRuleEditorInner;

export const CategoryIdRuleEditor = CategoryIdRuleEditorPrimitive;

export default CategoryIdRuleEditor;

export type CategoryIdRuleEditorGenericProps = WithClasses<
	CategoryIdRuleEditorClasses,
	CommonRuleEditorProps
>;

export const CategoryIdRuleEditorGenericPrimitive = ({
	elementValue1,
	...props
}: CategoryIdRuleEditorGenericProps) => {
	return (
		<CategoryIdRuleEditor
			{...props}
			elementValue1={parseInt(elementValue1.toString())}
		/>
	);
};

export const CategoryIdRuleEditorGeneric = CategoryIdRuleEditorGenericPrimitive;
